/** One web service's usage (`/analytics/web/:serviceId`): where sessions came
 *  from, time per screen, the feature heatmap (as a table, or over the
 *  service's own screens when it publishes a screen map), how far pages were read, and
 *  countries and devices. Every section is the shared layout's
 *  (internal/webusage), so a new service gets this page as it is. */

import type { ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { api, type WebUsageReport } from '@/api/client'
import { formatRelative, fromUnixMs } from '@/lib/time'
import { qk } from '@/lib/queries'
import { Bars, CountryTag, DataTable, Disclosure, EmptyState, ErrorState, FilterBar, Legend, MiniBar, Notice, PageHeader, Segmented, StatTile, useQueryParam, type Column } from '@/ui'
import { bucketLabel, fmtDuration, fmtNum, rangeLabel, rangeTitle, shortDay, windowLabel } from '../format'
import { useWindowDays } from '../params'
import { WindowPicker } from '../pickers'
import { Panel, Section } from '../Section'
import { ScreenHeat, useScreenMap } from './ScreenMap'
import { deviceLabel, heat, planLabel, ScopePicker, share, sourceLabel, surfaceLabel, useInternal } from './shared'

/** A service's page opens on a week: a day is too thin for a heatmap. */
export const WEB_DETAIL_DAYS = 7

type Source = WebUsageReport['sources'][number]
type Screen = WebUsageReport['screens'][number]
type Control = WebUsageReport['controls'][number]
type Page = WebUsageReport['pages'][number]
type Place = WebUsageReport['places'][number]

const num = 'tabular-nums'

/** A code-like value (a route, a control name) with where it lives under it. */
function Named({ code, under }: { code: string; under?: string }) {
  return (
    <span className="block min-w-0">
      <span className="block truncate font-mono text-table">{code || '—'}</span>
      {under && <span className="block truncate text-micro text-muted-foreground">{under}</span>}
    </span>
  )
}

function Heat({ v, top, children }: { v: number; top: number; children?: ReactNode }) {
  return (
    <span className="inline-block min-w-[2.5rem] rounded px-1.5 py-0.5 text-right tabular-nums" style={heat(v, top)}>
      {v > 0 ? (children ?? fmtNum(v)) : <span className="text-muted-foreground">·</span>}
    </span>
  )
}

export default function WebUsageDetail() {
  const { serviceId = '' } = useParams()
  const [days, setDays] = useWindowDays(WEB_DETAIL_DAYS)
  const [internal, setInternal] = useInternal()
  const rep = useQuery({
    queryKey: qk.webUsageReport(serviceId, days, internal),
    queryFn: () => api.webUsageReport(serviceId, days, internal),
    placeholderData: keepPreviousData,
  })
  const r = rep.data
  const t = r?.totals
  const back = new URLSearchParams()
  back.set('days', String(days))
  if (internal) back.set('internal', '1')

  return (
    <>
      <PageHeader
        title={r?.service.name ?? serviceId}
        docTitle={`${r?.service.name ?? serviceId} 사용량`}
        parent={[
          { to: `/analytics?days=${days}`, label: '사용량' },
          { to: `/analytics/web?${back}`, label: '웹 서비스' },
        ]}
        crumb={r?.service.name ?? serviceId}
        description={
          r?.service.url ? (
            <a href={r.service.url} target="_blank" rel="noreferrer" className="text-accent hover:underline">
              {r.service.url.replace(/^https:\/\//, '')}
            </a>
          ) : undefined
        }
      >
        <FilterBar
          end={
            t &&
            t.daily.length > 0 && (
              <p className="text-meta text-muted-foreground">
                <span title={rangeTitle(t.daily)}>{rangeLabel(t.daily)}</span>
              </p>
            )
          }
        >
          <WindowPicker days={days} onChange={setDays} />
          <ScopePicker internal={internal} onChange={setInternal} />
        </FilterBar>
      </PageHeader>

      {rep.isError ? (
        <ErrorState title="Cloudflare에서 읽지 못했다" error={rep.error} onRetry={() => rep.refetch()} />
      ) : !r || !t ? (
        <p className="text-table text-muted-foreground">불러오는 중</p>
      ) : (
        <div className="space-y-6">
          {!r.configured && (
            <Notice tone="neutral" title="Cloudflare 토큰이 아직 없다">
              <Link to="/settings?tab=webusage" className="text-accent hover:underline">
                설정의 웹 서비스 분석
              </Link>
              에 Account Analytics 읽기 권한 토큰을 넣으면 숫자가 나온다.
            </Notice>
          )}

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatTile label={`세션 · ${windowLabel(days)}`} value={fmtNum(t.sessions)} />
            <StatTile label="화면·페이지 조회" value={fmtNum(t.views)} />
            <StatTile label="세션당 이용" value={t.sessions > 0 ? fmtDuration(t.time_ms / t.sessions) : '—'} />
            <StatTile label="가장 새 기록" value={t.last_event_at ? formatRelative(fromUnixMs(t.last_event_at)) : '—'} hint="5분마다 새로 읽는다" />
          </div>

          <Section title="추이" aside={days === 1 ? 'UTC 시간별 세션' : 'UTC 일별 세션'}>
            <Panel>
              {t.sessions > 0 ? (
                <div className="space-y-2">
                  <Legend items={[{ label: '세션', swatch: 'bg-chart-1', value: t.sessions }]} />
                  <Bars
                    label="세션"
                    labels={t.daily.map((d) => d.day)}
                    outer={t.daily.map((d) => d.sessions)}
                    outerFill="bg-chart-1"
                    tick={shortDay}
                    height={96}
                    title={(i) => `${bucketLabel(t.daily[i].day)}\n세션 ${fmtNum(t.daily[i].sessions)}`}
                  />
                </div>
              ) : (
                <EmptyState title="이 기간에 세션이 없다" description="기간을 늘리거나 범위를 '운영자 포함'으로 바꾸면 보일 수 있다." />
              )}
            </Panel>
          </Section>

          <Sources rows={r.sources} />
          <Screens rows={r.screens} />
          <Heatmap serviceId={serviceId} rows={r.controls} plans={r.plans} />
          <Pages rows={r.pages} />
          <Places rows={r.places} />

          <Disclosure title="설명">
            <dl className="space-y-3 text-table">
              <div>
                <dt className="font-medium">이용 시간</dt>
                <dd className="mt-0.5 text-muted-foreground">
                  탭이 화면 앞에 있는 동안만 센다. 마지막으로 누르거나 입력하거나 스크롤한 뒤 3분이 지나면 멈춘다. 세션은 브라우저 탭 하나이고, 서비스 안의 구역(앱과 읽기
                  페이지처럼 주소가 다른 곳)마다 따로 센다.
                </dd>
              </div>
              <div>
                <dt className="font-medium">기능 히트맵</dt>
                <dd className="mt-0.5 text-muted-foreground">
                  기능 이름은 화면 코드에 붙은 이름(data-act 같은 것)이고, 글 내용은 보내지 않는다. 칸의 색이 진할수록 많이 눌렀다. 쓴 비율은 그 화면을 본 세션 중 한 번이라도
                  누른 세션의 비율이다.
                </dd>
              </div>
              <div>
                <dt className="font-medium">화면 위 히트맵</dt>
                <dd className="mt-0.5 text-muted-foreground">
                  서비스가 올린 화면 사진(주소/usage-map/map.json) 위에 기능마다 누른 횟수를 겹쳐 그린다. 누른 점이 아니라 그 기능이 있는 자리다. 원이 크고 진할수록 많이 눌렀고,
                  점선은 이 기간에 아무도 누르지 않은 기능이다. 데스크톱 사진에는 태블릿도 들어간다. 사진에 없는 기능(열지 않은 메뉴 안, 지금은 없는 버튼)은 오른쪽 아래에 따로
                  적는다. 화면이 바뀌면 서비스 쪽에서 사진을 다시 찍는다.
                </dd>
              </div>
              <div>
                <dt className="font-medium">페이지</dt>
                <dd className="mt-0.5 text-muted-foreground">
                  깊이는 세션마다 그 페이지를 가장 많이 내려간 곳이다. 끝까지는 90% 넘게 내려간 세션, 다음으로는 그 페이지에서 &lsquo;다음&rsquo;(next)을 누른 세션이다.
                </dd>
              </div>
              <div>
                <dt className="font-medium">범위</dt>
                <dd className="mt-0.5 text-muted-foreground">
                  기본은 방문자만이다. 관리자 계정으로 쓴 것과 배포 뒤 확인 방문(utm_source=verify)은 &lsquo;운영자 포함&rsquo;에서만 센다. 로그인이 없는 페이지에서는
                  운영자 본인 방문도 방문자로 잡힌다.
                </dd>
              </div>
            </dl>
          </Disclosure>
        </div>
      )}
    </>
  )
}

function Sources({ rows }: { rows: Source[] }) {
  const total = rows.reduce((n, x) => n + x.sessions, 0)
  const columns: Column<Source>[] = [
    { key: 'source', header: '어디서', card: 'title', cell: (x) => <span className="font-medium">{sourceLabel(x.source)}</span> },
    { key: 'surface', header: '구역', card: 'meta', cell: (x) => surfaceLabel(x.surface) },
    { key: 'sessions', header: '세션', align: 'right', className: num, cell: (x) => fmtNum(x.sessions) },
    { key: 'share', header: '비중', align: 'right', className: num, cell: (x) => share(x.sessions, total) },
    { key: 'per', header: '세션당 이용', align: 'right', className: num, cell: (x) => (x.sessions > 0 ? fmtDuration(x.time_ms / x.sessions) : '—') },
  ]
  return (
    <Section title="들어온 곳" aside="utm_source나 넘어온 사이트">
      <DataTable
        label="들어온 곳"
        rows={rows}
        rowKey={(x) => `${x.surface}|${x.source}`}
        columns={columns}
        empty={<EmptyState title="세션이 없다" />}
      />
    </Section>
  )
}

function Screens({ rows }: { rows: Screen[] }) {
  const total = rows.reduce((n, x) => n + x.time_ms, 0)
  const top = Math.max(0, ...rows.map((x) => x.time_ms))
  const columns: Column<Screen>[] = [
    { key: 'screen', header: '화면', card: 'title', cell: (x) => <Named code={x.screen} under={surfaceLabel(x.surface)} /> },
    { key: 'views', header: '조회', align: 'right', className: num, cell: (x) => fmtNum(x.views) },
    { key: 'per', header: '조회당 이용', align: 'right', className: num, cell: (x) => (x.views > 0 ? fmtDuration(x.time_ms / x.views) : '—') },
    {
      key: 'time',
      header: '전체 이용',
      align: 'right',
      cell: (x) => (
        <Heat v={x.time_ms} top={top}>
          {fmtDuration(x.time_ms)} <span className="text-muted-foreground">{share(x.time_ms, total)}</span>
        </Heat>
      ),
    },
    { key: 'sessions', header: '본 세션', align: 'right', className: num, cell: (x) => fmtNum(x.sessions) },
  ]
  return (
    <Section title="화면별 이용 시간" aside="오래 쓴 순">
      <DataTable
        label="화면별 이용 시간"
        rows={rows}
        rowKey={(x) => `${x.surface}|${x.screen}`}
        columns={columns}
        empty={<EmptyState title="본 화면이 없다" />}
      />
    </Section>
  )
}

function Heatmap({ serviceId, rows, plans }: { serviceId: string; rows: Control[]; plans: string[] }) {
  const map = useScreenMap(serviceId)
  const [raw, setView] = useQueryParam('heat')
  const pictured = !!map.data?.available
  const view = pictured && raw !== 'table' ? 'screen' : 'table'
  const top = Math.max(0, ...rows.map((x) => x.presses))
  const topPlan = Math.max(0, ...rows.flatMap((x) => Object.values(x.by_plan)))
  const columns: Column<Control>[] = [
    { key: 'control', header: '기능', card: 'title', cell: (x) => <Named code={x.control} under={`${surfaceLabel(x.surface)} · ${x.screen}`} /> },
    { key: 'presses', header: '누른 횟수', align: 'right', cell: (x) => <Heat v={x.presses} top={top} /> },
    {
      key: 'rate',
      header: <span title="그 화면을 본 세션 중 한 번이라도 누른 세션의 비율">쓴 비율</span>,
      align: 'right',
      cell: (x) => <MiniBar value={Math.round(x.use_rate * 100)} label="쓴 비율 %" />,
    },
    ...plans.map(
      (p): Column<Control> => ({
        key: `plan:${p}`,
        header: planLabel(p),
        align: 'right',
        card: 'hidden',
        cell: (x) => <Heat v={x.by_plan[p] ?? 0} top={topPlan} />,
      }),
    ),
  ]
  return (
    <Section
      title="기능 히트맵"
      aside={view === 'screen' ? '누른 횟수, 화면 위' : '누른 횟수, 요금제별'}
      actions={
        pictured && (
          <Segmented
            label="기능 히트맵 보기"
            size="sm"
            options={[
              { value: 'screen', label: '화면', title: '서비스 화면 사진 위에' },
              { value: 'table', label: '표', title: '요금제별 표' },
            ]}
            value={view}
            onChange={(v) => setView(v === 'table' ? 'table' : '')}
          />
        )
      }
    >
      {view === 'screen' && map.data ? (
        <ScreenHeat map={map.data} controls={rows} />
      ) : (
        <DataTable
          label="기능 히트맵"
          rows={rows}
          rowKey={(x) => `${x.surface}|${x.screen}|${x.control}`}
          columns={columns}
          empty={<EmptyState title="누른 기능이 없다" />}
        />
      )}
    </Section>
  )
}

function Pages({ rows }: { rows: Page[] }) {
  if (rows.length === 0) return null
  const top = Math.max(0, ...rows.map((x) => x.views))
  const columns: Column<Page>[] = [
    { key: 'path', header: '페이지', card: 'title', cell: (x) => <Named code={x.path} under={x.screen} /> },
    { key: 'views', header: '조회', align: 'right', cell: (x) => <Heat v={x.views} top={top} /> },
    { key: 'per', header: '조회당 이용', align: 'right', className: num, cell: (x) => (x.views > 0 ? fmtDuration(x.time_ms / x.views) : '—') },
    { key: 'depth', header: '평균 깊이', align: 'right', cell: (x) => <MiniBar value={Math.round(x.avg_depth)} label="평균 깊이 %" /> },
    { key: 'done', header: <span title="90% 넘게 내려간 세션의 비율">끝까지</span>, align: 'right', className: num, cell: (x) => share(x.done, x.sessions) },
    {
      key: 'next',
      header: <span title="이 페이지에서 다음(next)을 누른 세션의 비율">다음으로</span>,
      align: 'right',
      className: num,
      cell: (x) => (x.next > 0 ? share(x.next, x.sessions) : <span className="text-muted-foreground">—</span>),
    },
  ]
  return (
    <Section title="페이지" aside="조회 많은 순, 100개까지">
      <DataTable label="페이지" rows={rows} rowKey={(x) => x.path} columns={columns} />
    </Section>
  )
}

function Places({ rows }: { rows: Place[] }) {
  const total = rows.reduce((n, x) => n + x.sessions, 0)
  const columns: Column<Place>[] = [
    { key: 'country', header: '나라', card: 'title', cell: (x) => (x.country ? <CountryTag code={x.country} /> : <span className="text-muted-foreground">—</span>) },
    { key: 'device', header: '기기', card: 'meta', cell: (x) => deviceLabel(x.device) },
    { key: 'sessions', header: '세션', align: 'right', className: num, cell: (x) => fmtNum(x.sessions) },
    { key: 'share', header: '비중', align: 'right', className: num, cell: (x) => share(x.sessions, total) },
  ]
  return (
    <Section title="나라·기기" aside="세션">
      <DataTable
        label="나라·기기"
        rows={rows}
        rowKey={(x) => `${x.country}|${x.device}`}
        columns={columns}
        empty={<EmptyState title="세션이 없다" />}
      />
    </Section>
  )
}
